import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { aJson, faqPage } from '@/lib/jsonLd';

import SectionKicker from './SectionKicker';

/** `useLayoutEffect` en el navegador, `useEffect` en el prerender. Ninguno de
 *  los dos corre en el servidor; lo único que cambia es que React no avisa. */
const useLayoutEffectSeguro = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * FaqAcordeon — el acordeón de preguntas frecuentes que cierra nueve de las
 * once páginas del rediseño.
 *
 * ── POR QUÉ LA ALTURA SE MIDE EN JS ─────────────────────────────────────────
 *
 * El diseño anima `max-height` entre `0px` y un **260px fijo**. Cualquier
 * respuesta más alta que eso queda cortada a media frase, sin scroll y sin
 * aviso; en móvil, donde el texto ocupa el doble, se corta casi cualquiera.
 * Ese tope es el problema, no la propiedad.
 *
 * Así que se mantiene `max-height` —que es justo lo que ya preveía
 * `.v3-faq-body` en `landingV3.css`, con su apagado por
 * `prefers-reduced-motion` incluido— pero con el valor MEDIDO del contenido en
 * vez de un número inventado. Al terminar de abrir se suelta a `none` para que
 * el panel pueda crecer solo si el texto se recoloca después (una fuente que
 * carga tarde, un giro de pantalla).
 *
 * La alternativa moderna —animar `grid-template-rows` de `0fr` a `1fr`, sin
 * medir nada— es más corta, pero solo ANIMA en Chrome 107+, Safari 17.4+ y
 * Firefox 127+; en versiones anteriores abre de golpe. Medir funciona en todas,
 * y el código extra son quince líneas que ya están escritas.
 *
 * ── ACCESIBILIDAD ───────────────────────────────────────────────────────────
 *
 * El botón del diseño no declara que abra nada. Aquí lleva `aria-expanded` +
 * `aria-controls` y el panel su `role="region"`, así que un lector de pantalla
 * anuncia el estado en vez de leer solo la pregunta.
 *
 * `abiertaPorDefecto` respeta el original, que arranca con la primera abierta:
 * todo cerrado se lee como una lista de titulares sin contenido. Pulsar la
 * abierta la cierra (de ahí el `-1`).
 */
export default function FaqAcordeon({ titulo, kicker, preguntas, abiertaPorDefecto = 0 }) {
  const [abierta, setAbierta] = useState(abiertaPorDefecto);
  const idBase = useId();
  const bloqueFaq = faqPage(preguntas);
  /** Mapa índice → el <div> interior, para poder medir su alto real. */
  const contenidos = useRef(new Map());

  const ajustar = useCallback(() => {
    contenidos.current.forEach((contenido, i) => {
      const panel = contenido?.parentElement;
      if (!panel) return;
      // `scrollHeight` del hijo y no del panel: el panel está recortado, así
      // que su propio alto ya es el que estamos intentando calcular.
      const alto = `${contenido.scrollHeight}px`;

      if (i === abierta) {
        panel.style.maxHeight = alto;
        return;
      }
      // Al cerrar uno que ya estaba abierto y suelto a `none`: de `none` a
      // `0px` no hay nada que interpolar y el cierre saldría de golpe. Se le
      // devuelve su alto concreto, se fuerza el reflow y desde ahí sí anima.
      if (panel.style.maxHeight === 'none') {
        panel.style.maxHeight = alto;
        void panel.offsetHeight;
      }
      panel.style.maxHeight = '0px';
    });
  }, [abierta]);

  // `useLayoutEffect` y no `useEffect`: hay que fijar el `max-height` antes de
  // que el navegador pinte, o el panel parpadea abierto del todo y se cierra.
  //
  // En el prerender NO hay pintado que adelantarse ni `window` que medir, y
  // React avisa por consola una vez por página —cincuenta veces por build— de
  // que `useLayoutEffect` no hace nada en el servidor. El aviso es correcto y
  // aquí es inofensivo (el HTML estático sale con los paneles cerrados, que es
  // lo que toca), pero cincuenta avisos tapan el error del día que sí importe.
  useLayoutEffectSeguro(ajustar, [ajustar, preguntas]);

  useEffect(() => {
    window.addEventListener('resize', ajustar);
    return () => window.removeEventListener('resize', ajustar);
  }, [ajustar]);

  return (
    <section className="px-6 pt-[76px]">
      {/*
        El `FAQPage` de schema.org, aquí y no en el `<head>`.

        Google exige que un FAQ declarado como tal esté VISIBLE en la página, y
        la forma de que eso no deje de ser verdad es que el schema y lo que se
        pinta salgan del mismo `preguntas`. Puesto en el `<head>` habría que
        pasarle las preguntas desde fuera, y el día que alguien edite una
        respuesta en el fichero de copy el schema seguiría diciendo la anterior
        — sin error, sin aviso y sin forma de notarlo.

        `renderToString` lo recoge sin que el prerender tenga que hacer nada:
        para él es un nodo más del árbol.

        El `dangerouslySetInnerHTML` es obligatorio: React escaparía las comillas
        del JSON y dejaría un bloque que no parsea. Lo que lo hace seguro es que
        `aJson()` convierte todo `<` en `<`, así que dentro no puede
        aparecer la cadena `</script>` venga el texto de donde venga.
      */}
      {bloqueFaq ? (
        <script
          type="application/ld+json"
          data-seo="faq"
          dangerouslySetInnerHTML={{ __html: aJson(bloqueFaq) }}
        />
      ) : null}

      <div className="max-w-[800px] mx-auto">
        {kicker ? <SectionKicker>{kicker}</SectionKicker> : null}

        <h2 className="text-[26px] md:text-[31px] leading-[1.15] tracking-[-.025em] font-bold text-v3-ink text-center mt-7">
          {titulo}
        </h2>

        <div className="mt-7 border-t border-v3-line">
          {preguntas.map((f, i) => {
            const activa = abierta === i;
            const idPanel = `${idBase}-panel-${i}`;
            const idBoton = `${idBase}-boton-${i}`;

            return (
              <div key={f.q} className="border-b border-v3-line">
                <h3>
                  <button
                    type="button"
                    id={idBoton}
                    aria-expanded={activa}
                    aria-controls={idPanel}
                    onClick={() => setAbierta(activa ? -1 : i)}
                    className="w-full text-left flex items-center gap-4 py-5 px-1 cursor-pointer"
                  >
                    <span className="flex-1 text-[16.5px] font-bold tracking-[-.01em] text-v3-ink">
                      {f.q}
                    </span>
                    {/* Decorativo: quien no ve el signo ya tiene el
                        `aria-expanded` del botón. */}
                    <span
                      aria-hidden="true"
                      className="flex-none w-[26px] h-[26px] rounded-full bg-v3-sunken text-v3-body flex items-center justify-center text-[15px] font-semibold"
                    >
                      {activa ? '−' : '+'}
                    </span>
                  </button>
                </h3>

                <div
                  id={idPanel}
                  role="region"
                  aria-labelledby={idBoton}
                  className="v3-faq-body"
                  /* El `max-height` lo pone `ajustar` en un `useLayoutEffect`,
                     que corre ANTES del primer pintado: no hay parpadeo y no
                     hay un `style` de React peleando con la escritura
                     imperativa por el mismo atributo. */
                  onTransitionEnd={(e) => {
                    // Solo la del propio panel, no las que burbujean de dentro.
                    if (e.target !== e.currentTarget || !activa) return;
                    // Abierto y quieto: se suelta el tope para que el texto
                    // pueda recolocarse sin quedarse recortado.
                    e.currentTarget.style.maxHeight = 'none';
                  }}
                >
                  <div
                    ref={(el) => {
                      if (el) contenidos.current.set(i, el);
                      else contenidos.current.delete(i);
                    }}
                  >
                    <p className="text-[15.5px] leading-[1.65] text-v3-body pr-[46px] pb-[22px] pl-1">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
